/**
 * Map Info Panel - Shared logic for displaying layer feature information
 *
 * Usage in a Stimulus controller:
 *
 * import { mapInfoPanel } from './map_info_panel';
 *
 * // In connect():
 * mapInfoPanel.setup(this, this.infoPanelTarget, this.infoPanelContentTarget, this.map);
 *
 * // Controller methods (call these from data-action):
 * closeInfoPanel() { mapInfoPanel.close(); }
 */

export const mapInfoPanel = {
  controller: null,
  panelElement: null,
  contentElement: null,
  map: null,
  mapClickListener: null,

  /**
   * Initialize the panel with references to DOM elements and map
   */
  setup(controller, panelElement, contentElement, map) {
    this.controller = controller;
    this.panelElement = panelElement;
    this.contentElement = contentElement;
    this.map = map;

    // Listen for features click event from layer_manager (contains array of all features at click point)
    document.addEventListener('map-features-clicked', this.handleFeaturesClick.bind(this));

    // Listen for map clicks to close the panel
    if (map) {
      this.mapClickListener = map.addListener('click', () => {
        this.close();
      });
    }
  },

  /**
   * Handle features click event - receives all features at click point
   */
  handleFeaturesClick(event) {
    const { features } = event.detail;
    if (!features || features.length === 0) return;

    this.renderFeatures(features);
    this.open();
  },

  /**
   * Render array of features in the panel
   */
  renderFeatures(features) {
    if (!this.contentElement) return;

    const featuresHtml = features.map(feature => {
      const strokeColor = feature.strokeColor || '#173558';
      const strokeWeight = feature.strokeWeight || 1.5;
      const fillColor = feature.fillColor || strokeColor;
      const fillOpacity = feature.fillOpacity || 0.05;

      // Convert fill to rgba for background
      const bgColor = this.hexToRgba(fillColor, fillOpacity);
      const badgeLabel = feature.sourceType === 'aip' ? 'NATIONAL RESTRICTION'
          : feature.sourceType === 'aaz' ? 'AUTO APPROVAL ZONE'
          : feature.sourceType === 'notam' ? 'NOTAM'
          : 'LOCAL RESTRICTION';

      return `
        <div class="map-info-feature" style="border-left: ${strokeWeight * 2}px solid ${strokeColor}; background: ${bgColor};">
          <div class="source-badge">${badgeLabel}</div>
          ${feature.featureName ? `<div class="feature-name">${this.escapeHtml(feature.featureName)}</div>` : ''}
          ${feature.description ? `<div class="feature-description">${this.formatDescription(feature.description)}</div>` : ''}
        </div>
      `;
    }).join('');

    this.contentElement.innerHTML = featuresHtml;
  },

  /**
   * Convert hex color to rgba with opacity
   */
  hexToRgba(hex, opacity) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  },

  /**
   * Open the panel
   */
  open() {
    if (this.panelElement) {
      this.panelElement.classList.add('open');
    }
  },

  /**
   * Close the panel
   */
  close() {
    if (this.panelElement) {
      this.panelElement.classList.remove('open');
    }
  },

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  },

  /**
   * Format description text - escape HTML and convert newlines to <br> tags
   */
  formatDescription(text) {
    return this.escapeHtml(text).replace(/\n/g, '<br>');
  },

  /**
   * Cleanup - remove event listeners
   */
  teardown() {
    document.removeEventListener('map-features-clicked', this.handleFeaturesClick.bind(this));
    if (this.mapClickListener) {
      google.maps.event.removeListener(this.mapClickListener);
    }
    this.controller = null;
    this.panelElement = null;
    this.contentElement = null;
    this.map = null;
  }
};
